import React from 'react';
import DateRangeSelector, { type DateRangeValue } from './DateRangeSelector';
import MetricCard from './MetricCard';
import MetricsChart from './MetricsChart';
import { useOverviewMetrics } from './useOverviewMetrics';
import { computePresetRange } from '../../../utils/dateRange';

interface MetricsSectionProps {
  slug: string;
}

// Two-decimal currency formatter. All revenue values on the Overview render
// exact cents so the publisher can reconcile against the receipt figures.
const formatCurrencyExact = (cents: number): string =>
  (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatImpressions = (n: number): string => n.toLocaleString('en-US');

const formatPercent = (fraction: number): string =>
  fraction.toLocaleString('en-US', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const initialRange = (): DateRangeValue => {
  const { start, end } = computePresetRange('30days', new Date());
  return { preset: '30days', start, end };
};

const MetricsSection: React.FC<MetricsSectionProps> = ({ slug }) => {
  const [range, setRange] = React.useState<DateRangeValue>(initialRange);
  const [showNetInfo, setShowNetInfo] = React.useState(false);
  const { data, loading, error } = useOverviewMetrics({
    slug,
    startDate: range.start,
    endDate: range.end,
  });

  const totals = data?.totals ?? {
    grossCents: 0,
    netCents: 0,
    deliveredImpressions: 0,
    cpmCents: 0,
  };

  // Net Revenue popover math: Revenue − CTV Buyer's share = Net. The "share
  // %" is (gross − net) / gross, which collapses to the submission's
  // rev_share_percent when only one is in scope and to a delivery-weighted
  // blend across multiple. Mirrors the internal Self-Serve dashboard.
  const ctvShareCents = totals.grossCents - totals.netCents;
  const ctvSharePercent = totals.grossCents > 0 ? ctvShareCents / totals.grossCents : 0;

  return (
    <section className="mb-8">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Metrics</h2>
        <DateRangeSelector value={range} onChange={setRange} />
      </div>
      {error && (
        <p className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{error}</p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
        <MetricCard
          title="Revenue"
          value={formatCurrencyExact(totals.grossCents)}
          type="revenue"
          loading={loading}
        />
        <div
          className="relative"
          onMouseEnter={() => setShowNetInfo(true)}
          onMouseLeave={() => setShowNetInfo(false)}
        >
          <MetricCard
            title="Net Revenue"
            value={formatCurrencyExact(totals.netCents)}
            type="netRevenue"
            loading={loading}
          />
          {showNetInfo && (
            <div className="absolute z-20 mt-2 right-0 w-80 bg-white border border-blue-100 shadow-lg rounded-md p-4">
              <p className="text-sm font-semibold text-blue-700 mb-3">
                Net revenue details
              </p>
              {totals.grossCents > 0 ? (
                <div className="text-xs text-slate-700 space-y-1 tabular-nums">
                  <div className="flex justify-between">
                    <span>Revenue</span>
                    <span>{formatCurrencyExact(totals.grossCents)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>
                      − CTV Buyer&apos;s share ({formatPercent(ctvSharePercent)})
                    </span>
                    <span>−{formatCurrencyExact(ctvShareCents)}</span>
                  </div>
                  <div className="border-t border-slate-200 my-1" />
                  <div className="flex justify-between font-semibold">
                    <span>= Net Revenue</span>
                    <span>{formatCurrencyExact(totals.netCents)}</span>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-slate-500">
                  No revenue in this date range.
                </p>
              )}
            </div>
          )}
        </div>
        <MetricCard
          title="Impressions Delivered"
          value={formatImpressions(totals.deliveredImpressions)}
          type="impressions"
          loading={loading}
        />
        <MetricCard
          title="CPM"
          value={formatCurrencyExact(totals.cpmCents)}
          type="cpm"
          loading={loading}
        />
      </div>
      <MetricsChart data={data?.dailyChart ?? []} loading={loading} />
    </section>
  );
};

export default MetricsSection;
